import React, { useEffect, useRef, useState } from 'react';
import { BaseLayerView, getFramework, useIdentity } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { loadImageAsync } from '@/utils/file-kit';
import { IMAGE_LAYER_CANVAS_ID_PREFIX, IRenderLayerProps, IWatermarkLayerProps } from '@/version-types';
import { computeFullScreenWatermarkSize } from '../utils/watermark';
import './index.less';

interface IRenderWatermarkLayerProps extends IRenderLayerProps {
    showCursorBrush?: boolean;
}

const WatermarkLayer: React.FC<IRenderWatermarkLayerProps> = ({ layer, isNavigator, isThumbnail = false }) => {
    const identity = useIdentity();
    const framework = getFramework(identity);

    const fromCanvas = useRef<HTMLCanvasElement>(null);

    const [imgInfo, setImgInfo] = useState<{
        width?: number;
        height?: number;
        localUrl?: string;
    }>();
    const { width, height, id, data, materialIds, opacity } = layer;

    const isFull = useRef(false);

    const { isFullScreen, url, originUrl, size } = data as IWatermarkLayerProps;

    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        setIsInitialized(true);
    }, []);

    useEffect(() => {
        if (isThumbnail) return;

        if (isFullScreen) {
            framework.operate.delActiveLayer();
        } else {
            if (isInitialized) {
                framework.operate.setActiveLayer(layer.id);
            }
        }
    }, [data, isFullScreen, (data as IWatermarkLayerProps).url, opacity, isThumbnail]);
    // 如果base64为空,加载wasm,生成图片
    useEffect(() => {
        // 绘制时会取该属性做判断
        const fn = () => {
            const localUrl = base64Kit.getLocalUrl(url || originUrl);
            // setLocalUrl(localUrl);

            fromCanvas.current?.setAttribute('ready', 'false');
            loadImageAsync(localUrl).then((img) => {
                const { width, height } = img;
                if (fromCanvas.current) {
                    fromCanvas.current.width = width;
                    fromCanvas.current.height = height;
                }
                const ctx = fromCanvas.current?.getContext('2d');
                ctx?.clearRect(0, 0, width, height);
                ctx?.drawImage(img, 0, 0, width, height);
                fromCanvas.current?.setAttribute('ready', 'true');
            });
        };
        if (isFullScreen && url) {
            const base64 = base64Kit.getLocalUrl(url);
            loadImageAsync(base64).then((image) => {
                const { width: w, height: h } = computeFullScreenWatermarkSize(
                    size,
                    {
                        width: image.width,
                        height: image.height,
                    },
                    {
                        width,
                        height,
                    },
                );

                setImgInfo({
                    width: w,
                    height: h,
                });

                setTimeout(() => {
                    setImgInfo({
                        localUrl: base64,
                    });
                }, 200);

                isFull.current = true;
            });
            return;
        } else if (url || originUrl) {
            isFull.current = false;
            fn();
        }
    }, [url, isFullScreen, originUrl, size, width, height]);

    return (
        <>
            {isFullScreen ? (
                <BaseLayerView layer={{ ...layer, ...{ rotate: 0 } }} disableEvents={true} isNavigator={isNavigator}>
                    <div
                        className="watermark-full-screen"
                        style={{
                            backgroundImage: `url(${imgInfo?.localUrl})`,
                            opacity: imgInfo?.localUrl ? opacity : 0,
                            backgroundSize: `${imgInfo?.width}px ${imgInfo?.height}px`,
                        }}
                    ></div>
                </BaseLayerView>
            ) : (
                <BaseLayerView layer={layer} disableEvents={false} isNavigator={isNavigator}>
                    <canvas
                        style={{ width, height, opacity, pointerEvents: 'none' }}
                        id={`${IMAGE_LAYER_CANVAS_ID_PREFIX}${id}`}
                        ref={fromCanvas}
                    ></canvas>
                </BaseLayerView>
            )}
        </>
    );
};
export default React.memo(WatermarkLayer);
